import { useEffect, useRef, useState } from 'react';
import { X, Check, ChatText, PaperPlaneRight, Trash, ArrowCounterClockwise } from '@phosphor-icons/react';
import { formatDate } from '@/utils/datetime';

function initials(name) {
  const n = (name || '?').trim();
  return n.slice(0, 2);
}

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'たった今';
  if (diff < 3600) return `${Math.floor(diff / 60)}分前`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}時間前`;
  return formatDate(d);
}

function Thread({ c, active, currentUserId, isAdmin, onReply, onResolve, onDelete, onFocusRef }) {
  const [reply, setReply] = useState('');
  const ref = useRef(null);
  useEffect(() => { if (active && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [active]);
  const canDelete = isAdmin || c.author_id === currentUserId;
  return (
    <div ref={ref} data-testid={`kb-comment-thread-${c.id}`}
      className={`rounded-lg border p-3 ${active ? 'border-blue-400 ring-1 ring-blue-200' : 'border-zinc-200'} ${c.resolved ? 'opacity-60' : 'bg-white'}`}>
      {c.quote && <div className="mb-1.5 border-l-2 border-yellow-400 bg-yellow-50 px-2 py-1 text-xs text-zinc-500 line-clamp-2">{c.quote}</div>}
      <div className="flex items-center gap-1.5 text-xs">
        <span className="font-medium text-zinc-700"><span data-no-translate>{c.author_name}</span></span>
        <span className="text-zinc-400">{relTime(c.created_at)}</span>
        {c.resolved && <span className="ml-1 rounded bg-emerald-100 px-1 text-[9px] font-medium text-emerald-700">解決済み</span>}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-zinc-800">{c.text}</p>

      {(c.replies || []).map((r) => (
        <div key={r.id} className="mt-2 border-l border-zinc-200 pl-2">
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-zinc-600"><span data-no-translate>{r.author_name}</span></span>
            <span className="text-zinc-400">{relTime(r.created_at)}</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-zinc-700">{r.text}</p>
        </div>
      ))}

      <div className="mt-2 flex items-center gap-1.5">
        <input value={reply} onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && reply.trim()) { onReply(c, reply.trim()); setReply(''); } }}
          placeholder="返信…" data-testid={`kb-comment-reply-input-${c.id}`}
          className="flex-1 rounded-md border border-zinc-200 px-2 py-1 text-sm outline-none focus:border-blue-400" />
        <button type="button" disabled={!reply.trim()} onClick={() => { onReply(c, reply.trim()); setReply(''); }}
          data-testid={`kb-comment-reply-btn-${c.id}`}
          className="rounded-md p-1.5 text-blue-600 hover:bg-blue-50 disabled:text-zinc-300"><PaperPlaneRight size={15} /></button>
      </div>

      <div className="mt-2 flex items-center gap-3 text-xs">
        <button type="button" onClick={() => onResolve(c)} data-testid={`kb-comment-resolve-${c.id}`}
          className="inline-flex items-center gap-1 text-zinc-500 hover:text-emerald-600">
          {c.resolved ? <><ArrowCounterClockwise size={13} />未解決に戻す</> : <><Check size={13} />解決</>}
        </button>
        {canDelete && (
          <button type="button" onClick={() => onDelete(c)} data-testid={`kb-comment-delete-${c.id}`}
            className="inline-flex items-center gap-1 text-zinc-500 hover:text-rose-600"><Trash size={13} />削除</button>
        )}
      </div>
    </div>
  );
}

export default function CommentsPanel({ comments, open, onClose, activeId, currentUserId, isAdmin, onReply, onResolve, onDelete }) {
  if (!open) return null;
  const openThreads = comments.filter((c) => !c.resolved);
  const resolved = comments.filter((c) => c.resolved);
  return (
    <div className="fixed right-0 top-14 z-40 flex h-[calc(100vh-3.5rem)] w-full max-w-sm flex-col border-l border-zinc-200 bg-zinc-50 shadow-xl" data-testid="kb-comments-panel">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800"><ChatText size={17} className="text-blue-600" />コメント <span className="text-zinc-400">({openThreads.length})</span></div>
        <button type="button" onClick={onClose} data-testid="kb-comments-close" className="text-zinc-400 hover:text-zinc-700"><X size={18} /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {openThreads.length === 0 && resolved.length === 0 && (
          <p className="px-2 py-8 text-center text-sm text-zinc-400" data-testid="kb-comments-empty">
            まだコメントはありません。<br />本文を選択して「コメント」ボタンから追加できます。
          </p>
        )}
        {openThreads.map((c) => (
          <Thread key={c.id} c={c} active={c.comment_id === activeId} currentUserId={currentUserId} isAdmin={isAdmin}
            onReply={onReply} onResolve={onResolve} onDelete={onDelete} />
        ))}
        {resolved.length > 0 && (
          <>
            <div className="px-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">解決済み ({resolved.length})</div>
            {resolved.map((c) => (
              <Thread key={c.id} c={c} active={c.comment_id === activeId} currentUserId={currentUserId} isAdmin={isAdmin}
                onReply={onReply} onResolve={onResolve} onDelete={onDelete} />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

export { initials };
